import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ContainerCard } from "../../components/ui/cards";
import { Tag, Badge, Spin } from "antd";
import { Phone, ArrowLeft, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { AI_CALLER_CALL_DETAIL_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import dayjs from "dayjs";
import RecordingPlayer from "../engage/ai-caller/RecordingPlayer";

// ── helpers ───────────────────────────────────────────────────────────────────

const OUTCOME_COLORS = {
    demo_booked: "green",
    qualified: "blue",
    disqualified: "red",
    no_answer: "default",
    callback_requested: "orange",
};

const STATUS_BADGE = {
    in_progress: "processing",
    completed: "success",
    failed: "error",
};

const formatDuration = (seconds) => {
    if (!seconds) return "—";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
};

const SCREENING_LABELS = {
    requirement: "Requirement",
    team_size: "Team Size",
    timeline: "Timeline",
    decision_maker: "Decision Maker",
};

// ── main component ────────────────────────────────────────────────────────────

export default function AICallerCallDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { fetch } = useApiMethods();

    const [call, setCall] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadCall = async () => {
        setLoading(true);
        const res = await fetch(AI_CALLER_CALL_DETAIL_URL(id));
        if (res?.status === "success") {
            setCall(res.data);
        } else {
            ToastMsg("error", res?.message || "Failed to load call details");
        }
        setLoading(false);
    };

    useEffect(() => { loadCall(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <ContainerCard>
                <div className="flex items-center justify-center py-24">
                    <Spin size="large" />
                </div>
            </ContainerCard>
        );
    }

    if (!call) {
        return (
            <ContainerCard>
                <div className="flex flex-col items-center justify-center py-24">
                    <p className="text-gray-500 dark:text-gray-400">Call not found</p>
                    <Button onClick={() => navigate("/ai-caller/calls")} className="mt-4">Back to Call Logs</Button>
                </div>
            </ContainerCard>
        );
    }

    const transcript = call.transcript || [];
    const screening = call.screening_answers || {};

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center gap-3 mb-4 p-2">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/ai-caller/calls")}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl"
                >
                    <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </Button>
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                        <Phone className="h-5 w-5 text-teal-600" /> Call Detail
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        {call.customer_name || "Unknown"} — {call.phone || "—"}
                    </p>
                </div>
            </div>

            {/* Customer info card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mx-2 mb-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Customer Info</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Name</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{call.customer_name || "—"}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Phone</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{call.phone || "—"}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Stage</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1">
                            {call.stage_before || "—"}
                            <ChevronRight className="h-3 w-3 text-gray-400" />
                            {call.stage_after || "—"}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Outcome</p>
                        {call.outcome ? (
                            <Tag color={OUTCOME_COLORS[call.outcome] || "default"} className="mt-1">
                                {call.outcome.replace("_", " ").toUpperCase()}
                            </Tag>
                        ) : <p className="text-sm text-gray-500">—</p>}
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                        <Badge status={STATUS_BADGE[call.status] || "default"} text={call.status?.replace("_", " ") || "—"} />
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Duration</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDuration(call.duration_seconds)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Language</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{call.language || "—"}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Date</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {call.created_at ? dayjs(call.created_at).format("DD MMM YYYY, hh:mm A") : "—"}
                        </p>
                    </div>
                </div>
            </div>

            {/* AI Summary */}
            {call.ai_summary && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mx-2 mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">AI Summary</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{call.ai_summary}</p>
                </div>
            )}

            {/* Screening Answers */}
            {Object.keys(screening).length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mx-2 mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Screening Answers</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {Object.entries(SCREENING_LABELS).map(([key, label]) => (
                            screening[key] !== undefined && screening[key] !== null ? (
                                <div key={key} className="flex flex-col">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{String(screening[key])}</p>
                                </div>
                            ) : null
                        ))}
                    </div>
                </div>
            )}

            {/* Transcript */}
            {transcript.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mx-2 mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Transcript</h3>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                        {transcript.map((msg, idx) => {
                            const isHuman = msg.role === "human" || msg.role === "customer";
                            return (
                                <div
                                    key={idx}
                                    className={`flex ${isHuman ? "justify-start" : "justify-end"}`}
                                >
                                    <div
                                        className={`max-w-[75%] px-3 py-2 rounded-lg text-sm ${
                                            isHuman
                                                ? "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                                                : "bg-teal-500 text-white"
                                        }`}
                                    >
                                        <p className="text-[10px] font-medium mb-1 opacity-70">
                                            {isHuman ? "Customer" : "AI Agent"}
                                        </p>
                                        <p>{msg.text || msg.content || ""}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Recording */}
            {/* Why the call failed — the provider's own message. */}
            {call.error_message && (
                <div className="rounded-lg border border-red-300 dark:border-red-700/60 bg-red-50 dark:bg-red-900/15 p-4 mx-2 mb-2">
                    <h3 className="text-sm font-semibold text-red-800 dark:text-red-200">
                        {call.status === "failed" ? "Call failed" : "Call note"}
                    </h3>
                    <p className="text-[13px] text-red-700 dark:text-red-300 mt-1 break-words">{call.error_message}</p>
                </div>
            )}

            {call.recording_url && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mx-2 mb-2">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Recording</h3>
                    <RecordingPlayer
                        callId={call.id}
                        endpoint="ai-caller/recording/{id}/"
                        variant="expanded"
                        className="!mt-0"
                    />
                </div>
            )}
        </ContainerCard>
    );
}
